// The advisor's insurer & product catalogue (handoff v08 #14, schema 14).
//
// Insurer → Product category → Specific plan, per workspace (each advisor's own; a team workspace shares it under the
// workspace's role rules — the rows live in the workspace dataset and sync like any other table, never across
// workspaces). New policies are chosen through it (Insurer → Category → Plan → policy and coverage details), and a
// policy keeps its text fields (carrier / productName / category) as the denormalised display and history:
//   - renaming an insurer or plan changes the catalogue only, unless the advisor chooses «update existing policies»;
//   - deactivating hides an entry from new policies; policies that have it keep it (and the form still shows it);
//   - an entry is removed for good only while nothing uses it (no policy — deleted ones included — and no child).
// Seeding never invents products: it proposes only what the workspace already holds (Practice.insurers and the
// distinct carriers / products of existing policies, grouped by their category), and the advisor confirms.
import { DomainError, created, touched, put, clean, opt, type Ctx } from './cmd.ts';
import { allRowsOf, type Changeset, type Put, type WorkspaceView } from './changeset.ts';
import { searchKey } from './rules.ts';
import { LIFE_CATEGORIES, policyCategory } from './policy-engine.ts';
import { getPractice } from './setup.ts';
import { savePolicy, type PolicyInput } from './services.ts';
import { mergeChangesets } from './changeset.ts';
import {
  POLICY_BRANCHES, POLICY_CATEGORIES,
  type CatalogCategory, type CatalogInsurer, type CatalogPlan, type Policy, type PolicyBranch, type PolicyCategory, type RowTypes, type TableName,
} from './schema.ts';

export const CATALOG_KINDS = ['insurer', 'category', 'plan'] as const;
export type CatalogKind = typeof CATALOG_KINDS[number];
export const CATALOG_TABLE = { insurer: 'catalog_insurers', category: 'catalog_categories', plan: 'catalog_plans' } as const satisfies Record<CatalogKind, TableName>;
type Entry = CatalogInsurer | CatalogCategory | CatalogPlan;
const MAX_NAME = 120;

/** the branch a policy category belongs to ('other' belongs to neither) */
export function branchOfCategory(c: PolicyCategory | null | undefined): PolicyBranch | null {
  if (!c || c === 'other') return null;
  return (LIFE_CATEGORIES as readonly string[]).includes(c) ? 'life' : 'general';
}
const bySort = (a: Entry, b: Entry) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
const same = (a: string | null | undefined, b: string | null | undefined) => !!a && !!b && searchKey(a) === searchKey(b);

// --- readers --------------------------------------------------------------------------------------------------------
export function catalogInsurers(v: WorkspaceView, opts: { all?: boolean } = {}): CatalogInsurer[] {
  return v.rows('catalog_insurers').filter((x) => opts.all || x.active).slice().sort(bySort);
}
export function catalogCategories(v: WorkspaceView, insurerId: string | null | undefined, opts: { all?: boolean } = {}): CatalogCategory[] {
  if (!insurerId) return [];
  return v.where('catalog_categories', 'insurerId', insurerId).filter((x) => opts.all || x.active).slice().sort(bySort);
}
export function catalogPlans(v: WorkspaceView, categoryId: string | null | undefined, opts: { all?: boolean } = {}): CatalogPlan[] {
  if (!categoryId) return [];
  return v.where('catalog_plans', 'categoryId', categoryId).filter((x) => opts.all || x.active).slice().sort(bySort);
}
export const catalogIsEmpty = (v: WorkspaceView) => v.rows('catalog_insurers').length === 0;

/** policies (in use, not deleted) that point at each catalogue entry */
export function catalogUsage(v: WorkspaceView): Map<string, number> {
  const m = new Map<string, number>();
  const inc = (id: string | null | undefined) => { if (id) m.set(id, (m.get(id) ?? 0) + 1); };
  for (const p of v.rows('policies')) { inc(p.catalogInsurerId); inc(p.catalogCategoryId); inc(p.catalogPlanId); }
  return m;
}
export interface CatalogTreePlan { plan: CatalogPlan; policies: number }
export interface CatalogTreeCategory { category: CatalogCategory; policies: number; plans: CatalogTreePlan[] }
export interface CatalogTreeInsurer { insurer: CatalogInsurer; policies: number; categories: CatalogTreeCategory[] }
/** the whole catalogue (inactive entries included) with how many policies use each entry */
export function catalogTree(v: WorkspaceView): CatalogTreeInsurer[] {
  const use = catalogUsage(v);
  return catalogInsurers(v, { all: true }).map((insurer) => ({
    insurer, policies: use.get(insurer.id) ?? 0,
    categories: catalogCategories(v, insurer.id, { all: true }).map((category) => ({
      category, policies: use.get(category.id) ?? 0,
      plans: catalogPlans(v, category.id, { all: true }).map((plan) => ({ plan, policies: use.get(plan.id) ?? 0 })),
    })),
  }));
}

/** what the policy form offers: active entries, plus the policy's current ones even when deactivated since */
export interface CatalogSelection { insurerId?: string | null; categoryId?: string | null; planId?: string | null }
export function catalogChoices(v: WorkspaceView, sel: CatalogSelection, current: CatalogSelection = {}) {
  const keep = <T extends Entry>(list: T[], all: T[], id: string | null | undefined): T[] => {
    if (!id || list.some((x) => x.id === id)) return list;
    const cur = all.find((x) => x.id === id);
    return cur ? [...list, cur] : list;
  };
  const insurers = keep(catalogInsurers(v), catalogInsurers(v, { all: true }), current.insurerId);
  const categories = keep(catalogCategories(v, sel.insurerId), catalogCategories(v, sel.insurerId, { all: true }), current.categoryId);
  const plans = keep(catalogPlans(v, sel.categoryId), catalogPlans(v, sel.categoryId, { all: true }), current.planId);
  return { insurers, categories, plans };
}

/**
 * The policy fields a cascade choice fills in: the links, and the text copied from the catalogue at that moment
 * (carrier = insurer name, productName = plan name, category / branch from the catalogue category).
 */
export function catalogPolicyFields(v: WorkspaceView, sel: CatalogSelection): Pick<Policy, 'catalogInsurerId' | 'catalogCategoryId' | 'catalogPlanId'> & Partial<Pick<Policy, 'carrier' | 'productName' | 'category' | 'branch'>> {
  const ins = sel.insurerId ? v.get('catalog_insurers', sel.insurerId) : undefined;
  const cat = sel.categoryId ? v.get('catalog_categories', sel.categoryId) : undefined;
  const plan = sel.planId ? v.get('catalog_plans', sel.planId) : undefined;
  if (cat && cat.insurerId !== ins?.id) throw new DomainError('catalog.mismatch');
  if (plan && plan.categoryId !== cat?.id) throw new DomainError('catalog.mismatch');
  const out: ReturnType<typeof catalogPolicyFields> = { catalogInsurerId: ins?.id ?? null, catalogCategoryId: cat?.id ?? null, catalogPlanId: plan?.id ?? null };
  if (ins) out.carrier = ins.name;
  if (cat) { if (cat.policyCategory) out.category = cat.policyCategory; out.branch = cat.branch; }
  if (plan) { out.productName = plan.name; if (plan.branch) out.branch = plan.branch; }
  return out;
}

/** the links of a policy must be one consistent path of THIS workspace's catalogue (checked by savePolicy) */
export function checkCatalogLinks(v: WorkspaceView, p: Pick<Policy, 'catalogInsurerId' | 'catalogCategoryId' | 'catalogPlanId'>): void {
  const ins = p.catalogInsurerId ? v.get('catalog_insurers', p.catalogInsurerId) : undefined;
  const cat = p.catalogCategoryId ? v.get('catalog_categories', p.catalogCategoryId) : undefined;
  const plan = p.catalogPlanId ? v.get('catalog_plans', p.catalogPlanId) : undefined;
  if ((p.catalogInsurerId && !ins) || (p.catalogCategoryId && !cat) || (p.catalogPlanId && !plan)) throw new DomainError('catalog.not_found');
  if (cat && cat.insurerId !== p.catalogInsurerId) throw new DomainError('catalog.mismatch');
  if (plan && (plan.categoryId !== p.catalogCategoryId || plan.insurerId !== p.catalogInsurerId)) throw new DomainError('catalog.mismatch');
}

// --- grouping for reports ---------------------------------------------------------------------------------------------
export interface CatalogGroup { key: string; name: string; catalogId: string | null }
/**
 * The insurer a policy is counted under in reports: its catalogue insurer when linked (current name, so a rename
 * regroups history correctly), else the catalogue insurer whose name matches the free text, else the free text.
 */
export function policyInsurerGroup(v: WorkspaceView, p: Pick<Policy, 'carrier' | 'catalogInsurerId'>): CatalogGroup {
  const linked = p.catalogInsurerId ? v.get('catalog_insurers', p.catalogInsurerId) : undefined;
  if (linked) return { key: `c:${linked.id}`, name: linked.name, catalogId: linked.id };
  const text = opt(p.carrier);
  if (!text) return { key: '-', name: '—', catalogId: null };
  const byName = v.rows('catalog_insurers').find((x) => same(x.name, text));
  if (byName) return { key: `c:${byName.id}`, name: byName.name, catalogId: byName.id };
  return { key: `t:${searchKey(text)}`, name: text, catalogId: null };
}
/** the plan (product) a policy is counted under: its catalogue plan when linked, else the product text */
export function policyPlanGroup(v: WorkspaceView, p: Pick<Policy, 'productName' | 'catalogPlanId'>): CatalogGroup {
  const linked = p.catalogPlanId ? v.get('catalog_plans', p.catalogPlanId) : undefined;
  if (linked) return { key: `c:${linked.id}`, name: linked.name, catalogId: linked.id };
  return { key: `t:${searchKey(p.productName ?? '')}`, name: p.productName || '—', catalogId: null };
}
/** every name the policy's insurer is known by (its text and its catalogue insurer's current name) */
export function policyInsurerNames(v: WorkspaceView, p: Pick<Policy, 'carrier' | 'catalogInsurerId'> | undefined): string[] {
  if (!p) return [];
  const out = new Set<string>();
  if (opt(p.carrier)) out.add(clean(p.carrier));
  const linked = p.catalogInsurerId ? v.get('catalog_insurers', p.catalogInsurerId) : undefined;
  if (linked) out.add(linked.name);
  return [...out];
}

// --- commands -------------------------------------------------------------------------------------------------------
function nameOf(input: unknown): string {
  const n = clean(input).replace(/\s+/g, ' ');
  if (!n) throw new DomainError('catalog.name_required');
  if (n.length > MAX_NAME) throw new DomainError('catalog.name_too_long');
  return n;
}
function siblings(v: WorkspaceView, kind: CatalogKind, parentId: string | null): Entry[] {
  if (kind === 'insurer') return catalogInsurers(v, { all: true });
  if (kind === 'category') return catalogCategories(v, parentId, { all: true });
  return catalogPlans(v, parentId, { all: true });
}
const parentOf = (kind: CatalogKind, e: Entry): string | null => (kind === 'insurer' ? null : kind === 'category' ? (e as CatalogCategory).insurerId : (e as CatalogPlan).categoryId);
function noDuplicate(v: WorkspaceView, kind: CatalogKind, parentId: string | null, name: string, id?: string) {
  if (siblings(v, kind, parentId).some((x) => x.id !== id && same(x.name, name))) throw new DomainError('catalog.duplicate');
}
const nextOrder = (v: WorkspaceView, kind: CatalogKind, parentId: string | null) => siblings(v, kind, parentId).reduce((m, x) => Math.max(m, x.sortOrder + 1), 0);
function getEntry(v: WorkspaceView, kind: CatalogKind, id: string): Entry {
  const e = v.get(CATALOG_TABLE[kind], id) as Entry | undefined;
  if (!e) throw new DomainError('catalog.not_found');
  return e;
}
/** live (not deleted) policies linked to an entry */
function linkedPolicies(v: WorkspaceView, kind: CatalogKind, id: string): Policy[] {
  const f = kind === 'insurer' ? 'catalogInsurerId' : kind === 'category' ? 'catalogCategoryId' : 'catalogPlanId';
  return v.rows('policies').filter((p) => p[f] === id);
}
/** how many existing policies «update existing policies» would change after a rename */
export function catalogRenameImpact(v: WorkspaceView, kind: CatalogKind, id: string): number { return linkedPolicies(v, kind, id).length; }

export interface CatalogSaveOptions {
  /** «update existing policies»: also bring the text of the (live) policies linked to this entry in line with it
   *  (insurer → carrier, plan → productName, category → category / branch); deleted policies are never rewritten */
  updatePolicies?: boolean;
}

export interface CatalogInsurerInput { id?: string; name: string; code?: string | null; notes?: string | null }
export function saveCatalogInsurer(v: WorkspaceView, input: CatalogInsurerInput, ctx: Ctx, opts: CatalogSaveOptions = {}): Changeset {
  const name = nameOf(input.name);
  const cur = input.id ? getEntry(v, 'insurer', input.id) as CatalogInsurer : undefined;
  noDuplicate(v, 'insurer', null, name, cur?.id);
  const fields = { name, code: opt(input.code), notes: opt(input.notes) };
  const row = cur ? touched(ctx, cur, fields) : created<'catalog_insurers'>(ctx, { ...fields, sortOrder: nextOrder(v, 'insurer', null), active: true, deactivatedAt: null });
  const puts: Put[] = [put('catalog_insurers', row)];
  if (cur && opts.updatePolicies) for (const p of linkedPolicies(v, 'insurer', cur.id)) if (p.carrier !== name) puts.push(put('policies', touched(ctx, p, { carrier: name })));
  return { action: cur ? 'catalog.insurer_update' : 'catalog.insurer_create', entity: 'catalog_insurers', entityId: row.id, summary: name, puts, deletes: [] };
}

export interface CatalogCategoryInput { id?: string; insurerId: string; name: string; policyCategory?: PolicyCategory | null; branch?: PolicyBranch | null; notes?: string | null }
export function saveCatalogCategory(v: WorkspaceView, input: CatalogCategoryInput, ctx: Ctx, opts: CatalogSaveOptions = {}): Changeset {
  const name = nameOf(input.name);
  const cur = input.id ? getEntry(v, 'category', input.id) as CatalogCategory : undefined;
  const insurerId = cur?.insurerId ?? input.insurerId;
  if (!v.get('catalog_insurers', insurerId)) throw new DomainError('catalog.not_found');
  if (cur && input.insurerId && input.insurerId !== cur.insurerId) throw new DomainError('catalog.move_insurer');
  const pc = input.policyCategory ?? null;
  if (pc && !(POLICY_CATEGORIES as readonly string[]).includes(pc)) throw new DomainError('catalog.category');
  const branch = input.branch ?? branchOfCategory(pc) ?? cur?.branch ?? 'life';
  if (!(POLICY_BRANCHES as readonly string[]).includes(branch)) throw new DomainError('catalog.branch');
  noDuplicate(v, 'category', insurerId, name, cur?.id);
  const fields = { name, policyCategory: pc, branch, notes: opt(input.notes) };
  const row = cur ? touched(ctx, cur, fields) : created<'catalog_categories'>(ctx, { insurerId, ...fields, sortOrder: nextOrder(v, 'category', insurerId), active: true, deactivatedAt: null });
  const puts: Put[] = [put('catalog_categories', row)];
  if (cur && opts.updatePolicies) {
    for (const p of linkedPolicies(v, 'category', cur.id)) if ((pc && p.category !== pc) || p.branch !== branch) puts.push(put('policies', touched(ctx, p, { ...(pc ? { category: pc } : {}), branch })));
  }
  return { action: cur ? 'catalog.category_update' : 'catalog.category_create', entity: 'catalog_categories', entityId: row.id, summary: name, puts, deletes: [] };
}

export interface CatalogPlanInput { id?: string; categoryId: string; name: string; code?: string | null; branch?: PolicyBranch | null; notes?: string | null; activeFrom?: string | null; activeTo?: string | null }
export function saveCatalogPlan(v: WorkspaceView, input: CatalogPlanInput, ctx: Ctx, opts: CatalogSaveOptions = {}): Changeset {
  const name = nameOf(input.name);
  const cur = input.id ? getEntry(v, 'plan', input.id) as CatalogPlan : undefined;
  const categoryId = input.categoryId || cur?.categoryId || '';
  const cat = v.get('catalog_categories', categoryId);
  if (!cat) throw new DomainError('catalog.not_found');
  // a plan may move to another category of the same insurer (its policies keep their links to the old path otherwise)
  if (cur && cat.insurerId !== cur.insurerId) throw new DomainError('catalog.move_insurer');
  if (cur && cur.categoryId !== categoryId && linkedPolicies(v, 'plan', cur.id).length) throw new DomainError('catalog.in_use');
  if (input.branch && !(POLICY_BRANCHES as readonly string[]).includes(input.branch)) throw new DomainError('catalog.branch');
  const activeFrom = opt(input.activeFrom), activeTo = opt(input.activeTo);
  if (activeFrom && activeTo && activeTo < activeFrom) throw new DomainError('catalog.dates');
  noDuplicate(v, 'plan', categoryId, name, cur?.id);
  const fields = { categoryId, name, code: opt(input.code), branch: input.branch ?? null, notes: opt(input.notes), activeFrom, activeTo };
  const row = cur
    ? touched(ctx, cur, { ...fields, ...(cur.categoryId !== categoryId ? { sortOrder: nextOrder(v, 'plan', categoryId) } : {}) })
    : created<'catalog_plans'>(ctx, { insurerId: cat.insurerId, ...fields, sortOrder: nextOrder(v, 'plan', categoryId), active: true, deactivatedAt: null });
  const puts: Put[] = [put('catalog_plans', row)];
  if (cur && opts.updatePolicies) for (const p of linkedPolicies(v, 'plan', cur.id)) if (p.productName !== name) puts.push(put('policies', touched(ctx, p, { productName: name })));
  return { action: cur ? 'catalog.plan_update' : 'catalog.plan_create', entity: 'catalog_plans', entityId: row.id, summary: name, puts, deletes: [] };
}

/** deactivate (hidden from new policies; policies that have it keep it) or reactivate — never touches a policy */
export function setCatalogActive(v: WorkspaceView, kind: CatalogKind, id: string, active: boolean, ctx: Ctx): Changeset {
  const e = getEntry(v, kind, id);
  if (e.active === active) return { action: 'catalog.noop', puts: [], deletes: [] };
  const row = touched(ctx, e, { active, deactivatedAt: active ? null : ctx.now } as Partial<Entry>);
  return { action: active ? 'catalog.reactivate' : 'catalog.deactivate', entity: CATALOG_TABLE[kind], entityId: id, summary: e.name, puts: [put(CATALOG_TABLE[kind], row as never)], deletes: [] };
}

/** move an entry up (-1) or down (+1) among its siblings, or to `index` (drag and drop); renumbers the siblings */
export function moveCatalogEntry(v: WorkspaceView, kind: CatalogKind, id: string, to: { by?: number; index?: number }, ctx: Ctx): Changeset {
  const e = getEntry(v, kind, id);
  const list = siblings(v, kind, parentOf(kind, e));
  const from = list.findIndex((x) => x.id === id);
  const target = Math.max(0, Math.min(list.length - 1, to.index ?? from + (to.by ?? 0)));
  if (target === from) return { action: 'catalog.noop', puts: [], deletes: [] };
  const next = list.slice();
  next.splice(from, 1);
  next.splice(target, 0, e);
  const puts: Put[] = [];
  next.forEach((x, i) => { if (x.sortOrder !== i) puts.push(put(CATALOG_TABLE[kind], touched(ctx, x, { sortOrder: i } as Partial<Entry>) as never)); });
  return { action: 'catalog.reorder', entity: CATALOG_TABLE[kind], entityId: id, summary: e.name, puts, deletes: [] };
}

/** whether an entry is used: by any policy (soft-deleted ones included — they are history) or by a child entry */
export function catalogEntryInUse(v: WorkspaceView, kind: CatalogKind, id: string): boolean {
  const f = kind === 'insurer' ? 'catalogInsurerId' : kind === 'category' ? 'catalogCategoryId' : 'catalogPlanId';
  if ((allRowsOf(v, 'policies') as readonly Policy[]).some((p) => p[f] === id)) return true;
  if (kind === 'insurer') return v.where('catalog_categories', 'insurerId', id).length > 0;
  if (kind === 'category') return v.where('catalog_plans', 'categoryId', id).length > 0;
  return false;
}
/** remove an entry typed by mistake — only while nothing uses it (otherwise: deactivate) */
export function removeCatalogEntry(v: WorkspaceView, kind: CatalogKind, id: string): Changeset {
  const e = getEntry(v, kind, id);
  if (catalogEntryInUse(v, kind, id)) throw new DomainError('catalog.in_use');
  return { action: 'catalog.remove', entity: CATALOG_TABLE[kind], entityId: id, summary: e.name, puts: [], deletes: [{ table: CATALOG_TABLE[kind], id }] };
}

// --- "other / not in my catalogue" → add it -----------------------------------------------------------------------------
export interface CatalogPathInput { carrier: string; productName?: string | null; policyCategory?: PolicyCategory | null; branch?: PolicyBranch | null; categoryName?: string | null }
/**
 * Find or create Insurer → Category → Plan for what the advisor typed (names compared without case and accents; an
 * existing deactivated entry is reactivated rather than duplicated). The category is matched by name, else by the
 * policy category it maps to. Returns the change and the ids.
 */
export function ensureCatalogPath(v: WorkspaceView, input: CatalogPathInput, ctx: Ctx, labelOf: (c: PolicyCategory) => string = (c) => c): { cs: Changeset; insurerId: string; categoryId: string | null; planId: string | null } {
  const puts: Put[] = [];
  const carrier = nameOf(input.carrier);
  const insurers = catalogInsurers(v, { all: true });
  let ins: CatalogInsurer | undefined = insurers.find((x) => same(x.name, carrier));
  if (!ins) { ins = created<'catalog_insurers'>(ctx, { name: carrier, code: null, notes: null, sortOrder: insurers.reduce((m, x) => Math.max(m, x.sortOrder + 1), 0), active: true, deactivatedAt: null }); puts.push(put('catalog_insurers', ins)); }
  else if (!ins.active) puts.push(put('catalog_insurers', touched(ctx, ins, { active: true, deactivatedAt: null })));
  const pc = input.policyCategory ?? null;
  const catName = opt(input.categoryName) ?? (pc ? labelOf(pc) : null);
  if (!catName) return { cs: { action: 'catalog.add_from_policy', summary: carrier, puts, deletes: [] }, insurerId: ins.id, categoryId: null, planId: null };
  const cats = catalogCategories(v, ins.id, { all: true });
  let cat: CatalogCategory | undefined = cats.find((x) => same(x.name, catName)) ?? (opt(input.categoryName) ? undefined : cats.find((x) => !!pc && x.policyCategory === pc));
  if (!cat) {
    cat = created<'catalog_categories'>(ctx, { insurerId: ins.id, name: nameOf(catName), policyCategory: pc, branch: input.branch ?? branchOfCategory(pc) ?? 'life', notes: null, sortOrder: cats.reduce((m, x) => Math.max(m, x.sortOrder + 1), 0), active: true, deactivatedAt: null });
    puts.push(put('catalog_categories', cat));
  } else if (!cat.active) puts.push(put('catalog_categories', touched(ctx, cat, { active: true, deactivatedAt: null })));
  const product = opt(input.productName);
  if (!product) return { cs: { action: 'catalog.add_from_policy', summary: carrier, puts, deletes: [] }, insurerId: ins.id, categoryId: cat.id, planId: null };
  const plans = catalogPlans(v, cat.id, { all: true });
  let plan: CatalogPlan | undefined = plans.find((x) => same(x.name, product));
  if (!plan) {
    plan = created<'catalog_plans'>(ctx, { insurerId: ins.id, categoryId: cat.id, name: nameOf(product), code: null, branch: null, notes: null, activeFrom: null, activeTo: null, sortOrder: plans.reduce((m, x) => Math.max(m, x.sortOrder + 1), 0), active: true, deactivatedAt: null });
    puts.push(put('catalog_plans', plan));
  } else if (!plan.active) puts.push(put('catalog_plans', touched(ctx, plan, { active: true, deactivatedAt: null })));
  return { cs: { action: 'catalog.add_from_policy', summary: `${carrier} · ${product}`, puts, deletes: [] }, insurerId: ins.id, categoryId: cat.id, planId: plan.id };
}

/**
 * Save a policy chosen through the cascade (or typed as «other / not in my catalogue»). With `addToCatalog`, the typed
 * insurer / category / product are added to the catalogue first (or found there) and the policy is linked to them — one
 * change, one undo. Every coverage and policy detail of `input` is saved exactly as savePolicy does.
 */
export function savePolicyFromCatalog(v: WorkspaceView, input: PolicyInput, ctx: Ctx, opts: { addToCatalog?: boolean; categoryName?: string | null; labelOf?: (c: PolicyCategory) => string } = {}): Changeset {
  if (!opts.addToCatalog || !opt(input.carrier)) return savePolicy(v, input, ctx);
  const pc = input.category ?? policyCategory({ category: null, productName: input.productName, branch: input.branch });
  const path = ensureCatalogPath(v, { carrier: input.carrier!, productName: input.productName, policyCategory: pc, branch: input.branch, categoryName: opts.categoryName }, ctx, opts.labelOf);
  const pol = savePolicy(overlayView(v, path.cs), { ...input, catalogInsurerId: path.insurerId, catalogCategoryId: path.categoryId, catalogPlanId: path.planId }, ctx);
  return { ...mergeChangesets(pol.action, path.cs, pol), entity: pol.entity, entityId: pol.entityId, summary: pol.summary };
}

/** a view with a pending change laid over it (so one command can build on the rows another just created) */
export function overlayView(v: WorkspaceView, cs: Changeset): WorkspaceView {
  const byTable = new Map<string, Map<string, unknown>>();
  for (const p of cs.puts) { const m = byTable.get(p.table) ?? new Map(); m.set(p.row.id, p.row); byTable.set(p.table, m); }
  const dels = new Set(cs.deletes.map((d) => `${d.table}\u0000${d.id}`));
  const merge = <T extends TableName>(table: T, base: readonly RowTypes[T][]): RowTypes[T][] => {
    const m = byTable.get(table);
    const out = base.filter((r) => !dels.has(`${table}\u0000${(r as { id: string }).id}`)).map((r) => (m?.get((r as { id: string }).id) as RowTypes[T] | undefined) ?? r);
    if (m) for (const [id, r] of m) if (!base.some((b) => (b as { id: string }).id === id)) out.push(r as RowTypes[T]);
    return out;
  };
  return {
    rows: (t) => (byTable.has(t) || cs.deletes.some((d) => d.table === t) ? merge(t, v.rows(t)) : v.rows(t)),
    get: (t, id) => (id && dels.has(`${t}\u0000${id}`) ? undefined : (id ? byTable.get(t)?.get(id) as never : undefined) ?? v.get(t, id)),
    where: (t, f, val) => (byTable.has(t) || cs.deletes.some((d) => d.table === t) ? merge(t, v.rows(t)).filter((r) => (r as unknown as Record<string, unknown>)[f as string] === val) : v.where(t, f, val)),
    allRows: (t) => merge(t, allRowsOf(v, t)),
  };
}

// --- seeding from what the workspace already holds ----------------------------------------------------------------------
export interface CatalogSeedPlan { name: string; existingId: string | null; policies: number }
export interface CatalogSeedCategory { policyCategory: PolicyCategory; branch: PolicyBranch; name: string; existingId: string | null; plans: CatalogSeedPlan[] }
export interface CatalogSeedInsurer { name: string; existingId: string | null; fromPractice: boolean; policies: number; categories: CatalogSeedCategory[] }
export interface CatalogSeed { insurers: CatalogSeedInsurer[]; newInsurers: number; newCategories: number; newPlans: number; linkable: number }

/**
 * What «import from my policies» would add: the insurers of the practice profile and the distinct carriers of the
 * policies, each with the categories its policies are in and the distinct product names — duplicates merged without
 * case / accents, entries already in the catalogue marked (never added twice). Policies without an insurer are left out.
 */
export function catalogSeed(v: WorkspaceView, labelOf: (c: PolicyCategory) => string = (c) => c): CatalogSeed {
  const insurers = new Map<string, CatalogSeedInsurer>();
  const existingIns = catalogInsurers(v, { all: true });
  const insurerFor = (name: string, fromPractice: boolean) => {
    const k = searchKey(name);
    let x = insurers.get(k);
    if (!x) { const ex = existingIns.find((e) => same(e.name, name)); x = { name: ex?.name ?? name, existingId: ex?.id ?? null, fromPractice, policies: 0, categories: [] }; insurers.set(k, x); }
    if (fromPractice) x.fromPractice = true;
    return x;
  };
  for (const n of getPractice(v).insurers) insurerFor(n, true);
  let linkable = 0;
  for (const p of v.rows('policies')) {
    const carrier = opt(p.carrier);
    if (!carrier || p.catalogInsurerId) continue;
    linkable++;
    const ins = insurerFor(carrier, false);
    ins.policies++;
    const pc = policyCategory(p);
    let cat = ins.categories.find((c) => c.policyCategory === pc);
    if (!cat) {
      const exCats = ins.existingId ? catalogCategories(v, ins.existingId, { all: true }) : [];
      const ex = exCats.find((c) => c.policyCategory === pc) ?? exCats.find((c) => same(c.name, labelOf(pc)));
      cat = { policyCategory: pc, branch: ex?.branch ?? branchOfCategory(pc) ?? p.branch, name: ex?.name ?? labelOf(pc), existingId: ex?.id ?? null, plans: [] };
      ins.categories.push(cat);
    }
    const product = opt(p.productName);
    if (!product) continue;
    let plan = cat.plans.find((x) => same(x.name, product));
    if (!plan) {
      const ex = cat.existingId ? catalogPlans(v, cat.existingId, { all: true }).find((x) => same(x.name, product)) : undefined;
      plan = { name: ex?.name ?? product, existingId: ex?.id ?? null, policies: 0 };
      cat.plans.push(plan);
    }
    plan.policies++;
  }
  const list = [...insurers.values()];
  for (const i of list) { i.categories.sort((a, b) => a.name.localeCompare(b.name)); for (const c of i.categories) c.plans.sort((a, b) => a.name.localeCompare(b.name)); }
  return {
    insurers: list,
    newInsurers: list.filter((i) => !i.existingId).length,
    newCategories: list.reduce((s, i) => s + i.categories.filter((c) => !c.existingId).length, 0),
    newPlans: list.reduce((s, i) => s + i.categories.reduce((t, c) => t + c.plans.filter((p) => !p.existingId).length, 0), 0),
    linkable,
  };
}

/**
 * Apply the seed: add what is missing (in the order shown) and, with `link` (default), link the existing policies to
 * the entries they were grouped under — their text (carrier, product, category) is left exactly as it was.
 */
export function importCatalogSeed(v: WorkspaceView, ctx: Ctx, opts: { labelOf?: (c: PolicyCategory) => string; link?: boolean } = {}): Changeset {
  const labelOf = opts.labelOf ?? ((c: PolicyCategory) => c);
  const seed = catalogSeed(v, labelOf);
  let cs: Changeset = { action: 'catalog.import', puts: [], deletes: [] };
  let view = v;
  const ids = new Map<string, { ins: string; cats: Map<PolicyCategory, string>; plans: Map<string, string> }>();
  for (const i of seed.insurers) {
    // an insurer already in the catalogue is used as it is (a deactivated one stays deactivated)
    const first = i.existingId ? { cs: { action: '', puts: [], deletes: [] } as Changeset, insurerId: i.existingId } : ensureCatalogPath(view, { carrier: i.name }, ctx);
    cs = { ...cs, puts: [...cs.puts, ...first.cs.puts] }; view = overlayView(v, cs);
    const rec = { ins: first.insurerId, cats: new Map<PolicyCategory, string>(), plans: new Map<string, string>() };
    for (const c of i.categories) {
      const cat = c.existingId ? { cs: { action: '', puts: [], deletes: [] } as Changeset, categoryId: c.existingId } : ensureCatalogPath(view, { carrier: i.name, categoryName: c.name, policyCategory: c.policyCategory, branch: c.branch }, ctx);
      cs = { ...cs, puts: [...cs.puts, ...cat.cs.puts] }; view = overlayView(v, cs);
      rec.cats.set(c.policyCategory, cat.categoryId!);
      for (const p of c.plans) {
        if (p.existingId) { rec.plans.set(`${c.policyCategory}\u0000${searchKey(p.name)}`, p.existingId); continue; }
        const catRow = view.get('catalog_categories', cat.categoryId!)!;
        const plan = created<'catalog_plans'>(ctx, { insurerId: first.insurerId, categoryId: catRow.id, name: nameOf(p.name), code: null, branch: null, notes: null, activeFrom: null, activeTo: null, sortOrder: catalogPlans(view, catRow.id, { all: true }).reduce((m, x) => Math.max(m, x.sortOrder + 1), 0), active: true, deactivatedAt: null });
        cs = { ...cs, puts: [...cs.puts, put('catalog_plans', plan)] }; view = overlayView(v, cs);
        rec.plans.set(`${c.policyCategory}\u0000${searchKey(p.name)}`, plan.id);
      }
    }
    ids.set(searchKey(i.name), rec);
  }
  let linked = 0;
  if (opts.link !== false) {
    for (const p of v.rows('policies')) {
      const carrier = opt(p.carrier);
      if (!carrier || p.catalogInsurerId) continue;
      const rec = ids.get(searchKey(carrier));
      if (!rec) continue;
      const pc = policyCategory(p);
      const catId = rec.cats.get(pc) ?? null;
      const planId = catId && opt(p.productName) ? rec.plans.get(`${pc}\u0000${searchKey(p.productName)}`) ?? null : null;
      cs.puts.push(put('policies', touched(ctx, p, { catalogInsurerId: rec.ins, catalogCategoryId: catId, catalogPlanId: planId })));
      linked++;
    }
  }
  return { ...cs, summary: `${seed.newInsurers}/${seed.newCategories}/${seed.newPlans}/${linked}` };
}
